import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginContain, LoginFooter } from "../../style/login/Login";
import Logo from "../../assets/images/logo.png";
import seePasswordIcon from "../../assets/images/icons/eye.png";
import hidePasswordIcon from "../../assets/images/icons/eye1.png";
import { Loader } from "../../utils/loader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { apiCall } from "../../services/authServices";
import "../../css/NewPassword.css";
import { encryptPassword } from "../../utils/hashing";

function Newpassword() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate()
  const [formValues, setFormValues] = useState({
    password: "",
    confirmPassword: "",
  });

  const location = useLocation();
  const payloadData = location?.state?.data;

  // Password validation functions
  const validatePasswordCriteria = (password) => {
    return {
      minLength: password.length >= 10,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumeric: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      noSequential: !/(012|123|234|345|456|567|678|789|890|987|876|765|654|543|432|321|210|qwerty|asdf|zxcv|abcd|1234|9876)/i.test(password)
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    // Perform real-time validation for confirmPassword
    if (name === "confirmPassword") {
      if (value !== formValues.password) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          confirmPassword: "Passwords do not match",
        }));
      } else {
        setErrors((prevErrors) => {
          const { confirmPassword, ...rest } = prevErrors;
          return rest;
        });
      }
    }
  };

  const validateForm = () => {
    let formErrors = {};

    if (!formValues.password) {
      formErrors.password = "Password is required";
    } else {
      const criteria = validatePasswordCriteria(formValues.password);
      const isPasswordValid = Object.values(criteria).every(criterion => criterion);
      if (!isPasswordValid) {
        formErrors.password = "Password does not meet all criteria";
      }
    }

    if (!formValues.confirmPassword) {
      formErrors.confirmPassword = "Confirm password is required";
    } else if (formValues.password !== formValues.confirmPassword) {
      formErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      if (validateForm()) {
        const encrypted = encryptPassword(formValues.password)
        const payload = {
          userId: payloadData.userId,
          userType: payloadData.userType,
          OTP: payloadData.OTP,
          password: encrypted
        }
        console.log('payload: ', payload);
        const res = await apiCall({
          endpoint: "api/auth/reset-password",
          method: "POST",
          payload: payload,
        });
        if (res && res.success) {
          setTimeout(() => {
            navigate("/");
          }, 2000);
          toast.success("Password reset successfully");
        }
      }
    } catch (error) {
      console.log("Error: ", error);
      toast.error(error?.response?.data?.error ?? "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const passwordCriteria = validatePasswordCriteria(formValues.password);

  return (
    <>
      <LoginContain>
        <div className="LoginLogo">
          <img src={Logo} alt="logo" />
        </div>
        <div className="LoginCard">
          <div className="LoginTitle">
            <h4>Reset Password</h4>
            <p>EasyGo Audit support team is just a tap away!</p>
          </div>
          <form onSubmit={changePassword}>
            <div className="LoginGroup">
              <label htmlFor="password" className="forgot-label">
                Enter New Password
              </label>
              <div className="PasswrdInpt">
                <input
                  type={open ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formValues.password}
                  placeholder="Enter new password"
                  autoComplete="off"
                  onChange={handleChange}
                />
                {errors.password && <div className="error">{errors.password}</div>}
                <button
                  type="button"
                  onClick={() => setOpen(!open)}
                >
                  <img
                    src={open ? hidePasswordIcon : seePasswordIcon}
                    alt="Toggle visibility"
                  />
                </button>
              </div>
            </div>
            <div className="LoginGroup">
              <label htmlFor="confirmPassword" className="forgot-label">
                Confirm Password
              </label>
              <div className="PasswrdInpt">
                <input
                  type={open ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formValues.confirmPassword}
                  placeholder="Confirm password"
                  autoComplete="off"
                  onChange={handleChange}
                />
                {errors.confirmPassword && (
                  <div className="error">{errors.confirmPassword}</div>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(!open)}
                >
                  <img
                    src={open ? hidePasswordIcon : seePasswordIcon}
                    alt="Toggle visibility"
                  />
                </button>
              </div>
            </div>

            {/* Password Criteria Section */}
            <div className="password-criteria-container">
              <h5 className="criteria-title">Password Criteria:</h5>
              <div className="criteria-list">
                <div className={`criteria-item ${passwordCriteria.minLength ? 'valid' : 'invalid'}`}>
                  <span className="criteria-icon">{passwordCriteria.minLength ? '✓' : '×'}</span>
                  <span>Minimum 10 characters</span>
                </div>
                <div className={`criteria-item ${passwordCriteria.hasUppercase ? 'valid' : 'invalid'}`}>
                  <span className="criteria-icon">{passwordCriteria.hasUppercase ? '✓' : '×'}</span>
                  <span>Contains uppercase letters</span>
                </div>
                <div className={`criteria-item ${passwordCriteria.hasLowercase ? 'valid' : 'invalid'}`}>
                  <span className="criteria-icon">{passwordCriteria.hasLowercase ? '✓' : '×'}</span>
                  <span>Contains lowercase letters</span>
                </div>
                <div className={`criteria-item ${passwordCriteria.hasNumeric ? 'valid' : 'invalid'}`}>
                  <span className="criteria-icon">{passwordCriteria.hasNumeric ? '✓' : '×'}</span>
                  <span>Contains numeric characters</span>
                </div>
                <div className={`criteria-item ${passwordCriteria.hasSpecial ? 'valid' : 'invalid'}`}>
                  <span className="criteria-icon">{passwordCriteria.hasSpecial ? '✓' : '×'}</span>
                  <span>Contains special characters</span>
                </div>
                <div className={`criteria-item ${passwordCriteria.noSequential ? 'valid' : 'invalid'}`}>
                  <span className="criteria-icon">{passwordCriteria.noSequential ? '✓' : '×'}</span>
                  <span>No sequential patterns (e.g., 1234, qwerty)</span>
                </div>
              </div>
            </div>

            <div className="LoginGroup LoginBtn">
              <button type="submit" disabled={loading}>
                {loading ? <Loader /> : "Reset Password"}
              </button>
            </div>
          </form>
        </div>
        <ToastContainer />
      </LoginContain>

      <LoginFooter>
        <p>  POWERED BY: <span>Manthan IT Solutions</span></p>
      </LoginFooter>
    </>
  );
}

export default Newpassword;